import React from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  SITE,
  CANONICAL,
  TITLE,
  DESC,
  FAQ,
} from './presellContent';
import { normalizeVariant, DEFAULT_VARIANT, usePresellCtas } from './presellShared';
import RamboLayout from './RamboLayout';
import GrokLayout from './GrokLayout';
import FableLayout from './FableLayout';

import css from './PresellPage.module.css';

// ------------------------------------------------------------------
// BigLoads Owner Presell / Acquisition landing page.
//
//   Canonical URL: /list-your-trailer  (SEO head-intent + paid landing)
//   One coded route serves 3 GENUINELY DIFFERENT LAYOUTS for a Meta-paid
//   split test:
//     rambo -> RamboLayout   (rounded cards, airy, red-circle timeline)
//     grok  -> GrokLayout    (full-bleed hero, top-stripe cards, node rail)
//     fable -> FableLayout   (hairline dividers, spec-sheet, editorial)
//
//   COPY + CTA destinations + GA4 wiring + JSON-LD are IDENTICAL across
//   variants (shared: presellContent.js + presellShared.js). Only the
//   visual LAYOUT differs = clean A/B on design. The winner becomes the
//   permanent canonical page.
//
//   Every variant renders rel=canonical -> /list-your-trailer, and the two
//   non-canonical variants are noindex, so Google only indexes ONE page =
//   zero rotation penalty.
//
//   CTAs (wired in presellShared.usePresellCtas):
//     .cta-list-free -> owner signup flow (SignupPage, /signup)
//     .cta-haul-star -> Haul Star / subscription checkout
//   All CTA clicks fire GA4 presell_cta_click { variant, cta_location,
//   cta_type } for the split test.
// ------------------------------------------------------------------

const LAYOUTS = {
  rambo: RamboLayout,
  grok: GrokLayout,
  fable: FableLayout,
};

const PresellPageComponent = ({ scrollingDisabled, variant: variantProp }) => {
  const variant = normalizeVariant(variantProp);
  const { listFreeLink, haulStarLink, newsletterLink } = usePresellCtas(variant);

  const orgSchema = {
    '@type': 'Organization',
    name: 'BigLoads',
    url: SITE,
    description: DESC,
  };
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'List Your Trailer', item: CANONICAL },
    ],
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  // Non-canonical variants are noindex so only /list-your-trailer is indexed.
  const isCanonicalVariant = variant === DEFAULT_VARIANT;

  const Layout = LAYOUTS[variant] || RamboLayout;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[orgSchema, breadcrumbSchema, faqSchema]}
      canonicalUrl={CANONICAL}
      robots={isCanonicalVariant ? undefined : 'noindex,follow'}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        {/* Canonical is set on Page (canonicalUrl) so ALL variants point at
            /list-your-trailer. Non-canonical variants also get robots
            noindex via the Page `robots` prop above (guaranteed to reach
            <head> in SSR) so Google only indexes ONE page. */}
        <Layout
          listFreeLink={listFreeLink}
          haulStarLink={haulStarLink}
          newsletterLink={newsletterLink}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

PresellPageComponent.defaultProps = { variant: DEFAULT_VARIANT };
PresellPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  variant: string,
};

const mapStateToProps = state => ({ scrollingDisabled: isScrollingDisabled(state) });

const PresellPage = compose(connect(mapStateToProps))(PresellPageComponent);

export default PresellPage;
